import type { PrimaryFacetGroup, ProductFacet, SeriesFacet } from '../models/FacetModels';

export type FacetSelection = Map<number, Set<number>>;

export function parseFacetSelection(value: string): FacetSelection {
  const result: FacetSelection = new Map();
  value.split('-').filter(Boolean).forEach((group) => {
    const [attribute, values = ''] = group.split('_');
    const attributeSk = Number(attribute);
      const selected = values.split(',').map(Number).filter((item) => Number.isFinite(item) && item > 0);
    if (attributeSk > 0 && selected.length) result.set(attributeSk, new Set(selected));
  });
  return result;
}

export function hasFacetSelection(value: string, attributeSk: number, valueSk: number): boolean {
  return parseFacetSelection(value).get(attributeSk)?.has(valueSk) ?? false;
}

export function buildFacetSelection(selection: FacetSelection): string {
  return [...selection.entries()]
    .filter(([, values]) => values.size)
    .map(([attribute, values]) => `${attribute}_${[...values].join(',')}`)
    .join('-');
}

export function toggleFacetSelection(value: string, attributeSk: number, valueSk: number): string {
  const selection = parseFacetSelection(value);
  const values = selection.get(attributeSk) ?? new Set<number>();
  values.has(valueSk) ? values.delete(valueSk) : values.add(valueSk);
  values.size ? selection.set(attributeSk, values) : selection.delete(attributeSk);
  return buildFacetSelection(selection);
}

export function clearFacetGroup(value: string, attributeSk: number): string {
  const selection = parseFacetSelection(value);
  selection.delete(attributeSk);
  return buildFacetSelection(selection);
}

export function sortPrimarySeries(series: SeriesFacet[]): SeriesFacet[] {
  const preferred = ['nelson decodable readers', 'novastar', 'pm'];
  return [...series].sort((left, right) => {
    const a = preferred.indexOf((left.SeriesName ?? '').trim().toLowerCase());
    const b = preferred.indexOf((right.SeriesName ?? '').trim().toLowerCase());
    if (a >= 0 || b >= 0) return (a < 0 ? preferred.length : a) - (b < 0 ? preferred.length : b);
    return left.SeriesName.localeCompare(right.SeriesName);
  });
}

export function groupPrimaryFacets(facets: ProductFacet[]): PrimaryFacetGroup[] {
  const ndr = new Set(['Phonics Focus', 'Sounds']);
  const novaStar = new Set(['Learning Area', 'Recommended Age', 'Comprehension Strategy']);
  const groups: PrimaryFacetGroup[] = [
    { name: 'Nelson Decodable Readers', icon: '/portals/5/PRI-NDR-icon.svg', facets: [] },
    { name: 'NovaStar', icon: '/portals/5/PRI-NovaStar-icon.svg', facets: [] },
    { name: 'Others', facets: [] }
  ];
  facets.forEach((facet) => {
    const group = ndr.has(facet.AttributeType) ? groups[0] : novaStar.has(facet.AttributeType) ? groups[1] : groups[2];
    group.facets.push(facet);
  });
  return groups.filter((group) => group.facets.length);
}

export function filterFacetsForDivision(facets: ProductFacet[], division: string, countryCode: string): ProductFacet[] {
  const permitted: Record<string, string[]> = {
    secondary: ['Subject', 'Topic', 'Series', 'State', 'Year Level'],
    university: ['Format', 'Origin', 'Published Year', 'Publisher'],
    universityb2b: ['Format', 'Origin', 'Published Year', 'Publisher'],
    vet: ['Format', 'Origin', 'Published Year', 'Publisher'],
    vetb2b: ['Format', 'Origin', 'Published Year', 'Publisher']
  };
  const names = permitted[division.toLowerCase()];
  const filtered = names ? facets.filter((facet) => names.includes(facet.AttributeType)) : facets;
  return countryCode === 'NZ' && division.toLowerCase() === 'secondary'
    ? filtered.filter((facet) => facet.AttributeType !== 'State')
    : filtered;
}
